/** Chips, search field, rating stars and the stat row. */

import { useEffect, useRef, useState } from 'react';
import { compactNumber, decimal1 } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { haptic, hapticSelection } from '../hooks/useTg';
import { Icon } from './Icon';

/* ── Chips ───────────────────────────────────────────────────────────────── */

export function Chip({
  label,
  count,
  selected = false,
  onClick,
  chevron = false,
}: {
  label: string;
  /**
   * How many prompts are behind this chip, already formatted.
   *
   * Shown because a chip is a promise. Ordering the category row by size and printing the number
   * turns "Anime & Manga" from a guess into a decision, and it is the same figure the bot's menu
   * prints — one count, from one query.
   *
   * A **string**, not a number: a raw number renders Western digits, and in Farsi the chips read
   * "1158" beside engagement counts reading "۵۸۶" on the same screen. Formatting belongs with the
   * caller that knows the language.
   */
  count?: string;
  selected?: boolean;
  onClick?: () => void;
  /** Chips that open a sheet advertise it with a caret. */
  chevron?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => {
        hapticSelection();
        onClick?.();
      }}
      className={`flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-subhead font-medium transition-[background,color] duration-fast ease-pv ${
        selected ? 'bg-button text-button-text' : 'bg-surface text-text'
      }`}
    >
      <span>{label}</span>
      {count !== undefined && (
        <span className={`tabular-nums ${selected ? 'opacity-70' : 'text-hint'}`}>{count}</span>
      )}
      {chevron && <Icon name="chevronDown" size={13} className="flex-none opacity-70" />}
    </button>
  );
}

export function ChipRow({ children }: { children: React.ReactNode }) {
  return <div className="sc -mx-gutter flex gap-2 overflow-x-auto px-gutter">{children}</div>;
}

/* ── Search ──────────────────────────────────────────────────────────────── */

export function SearchBar({
  value,
  onChange,
  onSubmit,
  placeholder,
  autoFocus = false,
  /** Renders as a tappable shell that navigates to Search rather than a live field. */
  asButton = false,
  onPress,
}: {
  value?: string;
  onChange?: (next: string) => void;
  onSubmit?: () => void;
  placeholder: string;
  autoFocus?: boolean;
  asButton?: boolean;
  onPress?: () => void;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  if (asButton) {
    return (
      <button
        type="button"
        onClick={() => {
          haptic('light');
          onPress?.();
        }}
        className="flex h-[38px] w-full items-center gap-2 rounded-button bg-fill px-3 text-start text-hint"
      >
        <Icon name="search" size={17} className="flex-none" />
        <span className="truncate text-callout">{placeholder}</span>
      </button>
    );
  }

  return (
    <div className="flex h-[38px] w-full items-center gap-2 rounded-button bg-fill px-3 text-hint">
      <Icon name="search" size={17} className="flex-none" />
      <input
        ref={ref}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit?.();
        }}
        placeholder={placeholder}
        enterKeyHint="search"
        type="search"
        className="min-w-0 flex-1 border-0 bg-transparent text-callout text-text outline-none placeholder:text-hint"
      />
      {value ? (
        <button
          type="button"
          aria-label={t.clear}
          onClick={() => {
            haptic('light');
            onChange?.('');
          }}
          className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-chevron text-surface"
        >
          <Icon name="close" size={12} strokeWidth={3} />
        </button>
      ) : null}
    </div>
  );
}

/* ── Rating ──────────────────────────────────────────────────────────────── */

export function RatingStars({
  value,
  size = 14,
  className = '',
}: {
  value: number;
  size?: number;
  className?: string;
}) {
  const { t, lang } = useI18n();
  const rounded = Math.round(value);
  return (
    <span
      className={`flex items-center gap-0.5 ${className}`}
      aria-label={t.starsOf(decimal1(value, lang))}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Icon
          key={i}
          name="star"
          size={size}
          fill={i <= rounded}
          className={i <= rounded ? 'text-star' : 'text-chevron'}
        />
      ))}
    </span>
  );
}

/**
 * Interactive rating. Five 46px targets, optimistic fill on tap with a medium haptic; the
 * caller reconciles with the server and can roll the value back on failure.
 */
export function RatingInput({
  value,
  onRate,
  disabled = false,
}: {
  value: number;
  onRate: (stars: number) => void;
  disabled?: boolean;
}) {
  const { t, lang } = useI18n();
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  const canHover =
    typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches === true;

  return (
    <div
      dir="ltr"
      className="-ms-2 flex gap-0.5"
      onPointerLeave={() => setHover(0)}
      role="radiogroup"
      aria-label={t.rateThis}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          role="radio"
          aria-checked={value === i}
          aria-label={t.starsOf(compactNumber(i, lang))}
          disabled={disabled}
          onPointerEnter={() => canHover && setHover(i)}
          onClick={() => {
            haptic('medium');
            onRate(i);
          }}
          className="flex h-[46px] w-[46px] items-center justify-center transition-transform duration-200 ease-pv active:scale-[0.88] disabled:opacity-60"
        >
          <Icon
            name="star"
            size={30}
            fill={i <= shown}
            className={i <= shown ? 'text-star' : 'text-chevron'}
          />
        </button>
      ))}
    </div>
  );
}

/* ── Stats ───────────────────────────────────────────────────────────────── */

export function StatRow({
  ratingAvg,
  ratingCount,
  downloadCount,
  viewCount,
  className = '',
}: {
  ratingAvg: number;
  ratingCount: number;
  downloadCount: number;
  viewCount?: number;
  className?: string;
}) {
  const { lang } = useI18n();
  return (
    <div className={`tnum flex flex-wrap items-center gap-2 text-footnote text-hint ${className}`}>
      <span className="flex items-center gap-1 font-medium text-text">
        <Icon name="star" size={13} fill className="text-star" />
        {decimal1(ratingAvg, lang)}
      </span>
      <span>({compactNumber(ratingCount, lang)})</span>
      <span aria-hidden="true" className="opacity-45">
        ·
      </span>
      <span className="flex items-center gap-1">
        <Icon name="download" size={13} />
        {compactNumber(downloadCount, lang)}
      </span>
      {viewCount !== undefined && (
        <>
          <span aria-hidden="true" className="opacity-45">
            ·
          </span>
          <span className="flex items-center gap-1">
            <Icon name="view" size={13} />
            {compactNumber(viewCount, lang)}
          </span>
        </>
      )}
    </div>
  );
}

export function ModelBadge({ name }: { name: string }) {
  return (
    <span className="rounded-thumb bg-accent-tint px-2 py-1 text-footnote font-medium text-accent">
      {name}
    </span>
  );
}

export function TagPill({ tag, onClick }: { tag: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light');
        onClick?.();
      }}
      className="bidi-plaintext rounded-full bg-surface px-3 py-1.5 text-footnote text-accent transition-colors duration-fast ease-pv active:bg-accent-tint"
    >
      {/* The "#" is bidi-neutral: without isolation it flips to the far end of a latin tag
          inside an RTL page, rendering as "film grain#". */}
      <bdi>#{tag}</bdi>
    </button>
  );
}
